import { IoClose } from 'react-icons/io5';
import React from 'react';
import {
  handleAdjustGuestIndexes,
  removeEventTicketFromCart,
} from '../../ShoppingCartSliceV2';
import { Product } from '../../interfaces';
import { RootState } from '../../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import ProductPreviewComponent from '../../widgets/ProductPreviewComponent';
import { eventLink } from '../../../../utils/LinkGenerator';
import moment from 'moment';
import { currencySignsObj } from '@skygroup/shared/utils/constants';
import { Divider } from 'antd';
import { FaTruck } from 'react-icons/fa';
import { MainProductType } from '../../interfaces';

interface Props {
  product: any; // Product;
  identifier: string;
  isCheckoutPage: boolean;
  showProductPrice?: boolean;
  mainProductType?: MainProductType;
}

const ShoppingCartEvent = ({
  product,
  identifier,
  isCheckoutPage,
  showProductPrice = true,
  mainProductType,
}: Props) => {
  const dispatch = useDispatch();
  const { siteConfig } = useSelector((data: RootState) => data.config.config);
  const { shoppingCartTranslations: t, general: general_t, sportsAndEvents: sportsAndEvents_t } = useSelector(
    (data: RootState) => data.config.translations
  );
  const { ShoppingCartV2 } = useSelector(
    (state: RootState) => state.shoppingCartV2
  );

  const eventInfo = product?.product_data?.eventInfo;

  function generateLink(obj: any) {
    const startDate = moment(
      (obj?.date_start || obj?.subProducts?.[0]?.date_start) * 1000
    ).utc();
    const endDate = moment(
      (obj?.date_end || obj?.subProducts?.[0]?.date_end) * 1000
    ).utc();

    const formattedStartDate = startDate.format(siteConfig.dateFormat);
    const formattedEndDate = endDate.format(siteConfig.dateFormat);
    const link = `?from=${encodeURIComponent(
      formattedStartDate
    )}&to=${encodeURIComponent(formattedEndDate)}`;

    return link;
  }

  const handleDeleteEvent = (
    productIdentifier: string,
    subProductId: string
  ) => {
    dispatch(
      removeEventTicketFromCart({
        eventIdentifier: productIdentifier,
        eventTicketId: subProductId,
      })
    );

    if (product?.subProducts?.length < 2) {
      // if this condition is met, it means its a final ticket of the event,
      // and upon removing it we need to update g_indexes of the shopping cart products
      dispatch(handleAdjustGuestIndexes(product));
    }
  };

  return (
    <div key={identifier} className="product-top event-product">
      <div className="product-title">
        <ProductPreviewComponent
          productPath={eventLink(identifier, generateLink(product))}
          label={product.name}
          showActionButtons={mainProductType !== MainProductType.DynamicPackage}
        />
      </div>

      {eventInfo && (
        <div className="event-info">
          {eventInfo.venue && (
            <div className="event-venue">
              <b>{general_t?.venue}: </b>
              <span>{eventInfo.venue.name}</span>
              {eventInfo.venue.address && (
                <span className="venue-address">
                  {' '}
                  - {eventInfo.venue.address}
                </span>
              )}
            </div>
          )}
          {eventInfo.city && eventInfo.country && (
            <div className="event-location">
              <b>{general_t?.location}: </b>
              <span>
                {eventInfo.city.name}, {eventInfo.country.name}
              </span>
            </div>
          )}
        </div>
      )}

      <div
        className={'sub-products-title'}
      >{`${general_t?.tickets} ${t?.details}`}</div>

      <div className="subProduct-wrapper">
        {!!product?.subProducts?.length &&
          product.subProducts.map((subProduct: Product, ii: number) => {
            const ticketData = subProduct?.product_data?.ticketDetails;
            const deliveryMethod = subProduct?.product_data?.deliveryMethod;

            return (
              <div
                className="subProduct-row"
                key={`${identifier}_${subProduct.id}`}
              >
                {ii > 0 && <Divider dashed />}
                {subProduct?.name && (
                  <div>
                    <b>{general_t?.ticketType}: </b>
                    <span className="title">{subProduct?.name}</span>
                  </div>
                )}

                {(subProduct?.date_start || product?.date_start) && (
                  <div>
                    <b>{general_t?.date}: </b>{' '}
                    <span className="title">
                      {moment(
                        (subProduct?.date_start || product?.date_start) * 1000
                      ).format(siteConfig.dateFormat)}
                    </span>
                    {eventInfo?.timeOfEvent && (
                      <span className="event-time">
                        {eventInfo.timeOfEvent}
                      </span>
                    )}
                  </div>
                )}

                <div>
                  <b>{sportsAndEvents_t?.pricePerTicket}: </b>
                  <span className="title">{subProduct.product_price}</span>
                </div>

                {subProduct?.product_amount &&
                  subProduct.product_amount > 1 && (
                    <div>
                      <b>{general_t?.quantity}: </b>
                      <span className="title">
                        x {subProduct.product_amount}
                      </span>
                    </div>
                  )}

                {deliveryMethod && (
                  <div className="delivery-method-info">
                    <b>
                      <FaTruck style={{ marginRight: '4px' }} />
                      {general_t?.deliveryMethod}:{' '}
                    </b>
                    <span className="title">
                      {deliveryMethod.name}
                      {deliveryMethod.price > 0 && (
                        <span className="delivery-price">
                          {' '}
                          (+
                          {currencySignsObj?.[
                            ticketData?.currency?.toLowerCase() as keyof typeof currencySignsObj
                          ] ?? ticketData?.currency}
                          {deliveryMethod.price})
                        </span>
                      )}
                    </span>
                  </div>
                )}

                <div className={'last-row'}>
                  <div className="product-guests">
                    {subProduct?.related_guest_indexes?.length > 0 && (
                      <div className="icon-box">
                        <b>
                          {general_t?.amount}: x{' '}
                          {subProduct.related_guest_indexes.length}
                        </b>
                      </div>
                    )}
                  </div>
                  {!ShoppingCartV2.tp_id && subProduct?.total_price && (
                    <div className="price-box">
                      {showProductPrice && (
                        <span>{`${subProduct?.total_price?.toFixed()} ${
                          currencySignsObj?.[
                            ticketData?.currency?.toLowerCase() as keyof typeof currencySignsObj
                          ] ??
                          ticketData?.currency ??
                          ShoppingCartV2.currency_sign
                        }`}</span>
                      )}
                      {!isCheckoutPage &&
                        mainProductType !== MainProductType.DynamicPackage && (
                          <IoClose
                            size={15}
                            className="delete-product"
                            onClick={() =>
                              handleDeleteEvent(identifier, subProduct.id)
                            }
                          />
                        )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default ShoppingCartEvent;
